import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, MicIcon } from 'lucide-react';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { ProcessingTag } from '../ui/ProcessingTag';
import { useDb } from '../../hooks/useDb';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { avatarThreads } from '../../utils/dbSelectors';
import type { DeskStage } from './useDeskStage';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Left third: Anna (smaller, same living states) and the conversation stream. */
export function ConversationPanel({
  stage
}: {
  stage: DeskStage;
}) {
  const threads = useDb(avatarThreads);
  const suggestions = useDb(t => t.deskSuggestions);
  const [value, setValue] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rec = useSpeechRecognition(text => stage.ask(text, 'stem'));
  const {
    setListening
  } = stage;
  useEffect(() => setListening(rec.listening), [rec.listening, setListening]);
  useEffect(() => {
    if (rec.error) setNote(rec.error === 'no-speech' ? 'Ik hoorde niets. Probeer het nog eens.' : 'Microfoon werkt hier niet. Typ je vraag.');
  }, [rec.error]);
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [stage.stream.length, stage.revealed?.words]);
  const submit = (text: string) => {
    const t = text.trim();
    if (!t) return;
    setValue('');
    setNote(null);
    stage.ask(t, 'getypt');
  };
  const onMic = () => {
    if (rec.listening) return rec.stop();
    stage.bargeIn();
    setNote(null);
    if (!rec.start()) setNote('Spraakherkenning werkt niet in deze browser. Typ je vraag.');
  };
  return <section aria-label="Gesprek met Anna" className="flex min-h-0 flex-col" data-id="element-413" data-mp="3k8h8fwj4zrqt">
      <div className="flex items-center gap-5" data-id="element-414" data-mp="3f7tjdgegxkz8">
        <div className="aspect-square w-[180px] shrink-0 2xl:w-[230px]" data-id="element-415" data-mp="3f708q4ce2bbt">
          <AnnaAvatar state={stage.avatarState} threads={threads} pulseKey={stage.pulseKey} data-id="element-416" data-mp="279xkfc8lf9zx" />
        </div>
        <div className="min-w-0" data-id="element-417" data-mp="2b96cmcnziztd">
          <p className="font-display text-[26px] font-light text-ink" data-id="element-418" data-mp="2b8d9rms5cugi">Anna</p>
          <p className="text-[15px] text-ink-soft" data-id="element-419" data-mp="1xa6pxak18uzu">
            {stage.avatarState === 'spreekt' ? 'spreekt' : stage.avatarState === 'denkt' ? 'denkt na' : stage.avatarState === 'luistert' ? 'luistert' : stage.avatarState === 'bezorgd' ? 'let op' : stage.avatarState === 'gestopt' ? 'gestopt' : 'werkt mee'}
          </p>
          <AnimatePresence data-id="element-420" data-mp="1jjjo39iz6ozj">
            {stage.activity === 'thinking' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.15
          }} className="mt-2" data-id="element-421" data-mp="14nc4aabhbpks">
                <ProcessingTag where={stage.thinkingWhere} data-id="element-422" data-mp="pzc3cfb98gqg" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <div ref={listRef} className="no-scrollbar mt-4 min-h-0 flex-1 space-y-5 overflow-y-auto pr-1" aria-live="polite" data-id="element-423" data-mp="lz1xd2ngpqbl">
        {stage.stream.map(line => {
        const isLive = stage.revealed?.id === line.id;
        const words = line.text.split(' ');
        return <motion.div key={line.id} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} className={line.from === 'jij' ? 'pl-10' : ''} data-id="element-424" data-mp="18x4ukkcin280">
              {line.from === 'anna' ? <p className="font-display text-[21px] font-light leading-snug text-ink 2xl:text-[23px]" data-id="element-425" data-mp="1dq7tz93z55w4">
                  {isLive ? <>
                      {words.slice(0, stage.revealed?.words ?? 0).join(' ')}
                      <span className="sr-only" data-id="element-426" data-mp="3h5t8ib7zd57z">{words.slice(stage.revealed?.words ?? 0).join(' ')}</span>
                    </> : line.text}
                </p> : <div className="rounded-2xl bg-white/[0.05] px-4 py-3" data-id="element-427" data-mp="3qwnudehbe2sw">
                  <p className="text-[15px] text-ink" data-id="element-428" data-mp="3vufxl4netbce">{line.text}</p>
                  <p className="mt-1 text-[13px] text-ink-muted" data-id="element-429" data-mp="3vwsb95m4wzjn">
                    jij · {line.via} · {line.time}
                  </p>
                </div>}
            </motion.div>;
      })}
        {rec.listening && <p className="pl-10 text-[15px] italic text-ink-soft" data-id="element-430" data-mp="384llipawzqt7">{rec.interim || 'Ik luister…'}</p>}
      </div>

      <div className="mt-4" data-id="element-431" data-mp="3d4hqaqrfrm9t">
        <ul className="no-scrollbar mb-3 flex gap-2 overflow-x-auto" data-id="element-432" data-mp="3d5su2qkvsu0x">
          {suggestions.map(s => <li key={s} className="shrink-0" data-id="element-433" data-mp="nemjsxkgqdz1">
              <button type="button" onClick={() => submit(s)} className="whitespace-nowrap rounded-full border border-white/10 px-3 py-1.5 text-[13px] text-ink-soft transition-colors duration-150 hover:border-white/25 hover:text-ink" data-id="element-434" data-mp="sdh8uh90wyrl">
                {s}
              </button>
            </li>)}
        </ul>
        <form onSubmit={e => {
        e.preventDefault();
        submit(value);
      }} className="glass flex items-center gap-2 rounded-full py-1.5 pl-5 pr-1.5" data-id="element-435" data-mp="1y751rd3cscna">
          <label htmlFor="desk-ask" className="sr-only" data-id="element-436" data-mp="1p6p089667fl2">Vraag Anna iets</label>
          <input id="desk-ask" value={value} onChange={e => setValue(e.target.value)} placeholder="Vraag Anna iets…" autoComplete="off" className="min-w-0 flex-1 bg-transparent text-[16px] text-ink placeholder:text-ink-muted focus:outline-none" data-id="element-437" data-mp="1p8te9jxs9uz2" />
          <button type="button" onClick={onMic} aria-pressed={rec.listening} aria-label={rec.listening ? 'Stop met luisteren' : 'Praat met Anna'} className={`grid h-11 w-11 shrink-0 place-items-center rounded-full transition-colors duration-150 ${rec.listening ? 'bg-accent text-night-950' : 'text-ink-soft hover:bg-white/10 hover:text-ink'}`} data-id="element-438" data-mp="39lsbbtc4cd37">
            <MicIcon className="h-5 w-5" data-id="element-439" data-mp="12yb7gvf4v6mb" />
          </button>
          <button type="submit" aria-label="Versturen" disabled={!value.trim()} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent text-night-950 transition-opacity duration-150 disabled:opacity-40" data-id="element-440" data-mp="1qncmj1ih1g0k">
            <ArrowUpIcon className="h-5 w-5" strokeWidth={2.25} data-id="element-441" data-mp="1lnzywejs04wx" />
          </button>
        </form>
        {note && <p className="mt-2 text-[13px] text-amber" data-id="element-442" data-mp="5a02hdk52xef">{note}</p>}
      </div>
    </section>;
}